import styles from '../Account.module.css';
import AccountCard, { InfoRow } from '../AccountCard';
import SectionHead from '../SectionHead';
import StatusBadge from '../StatusBadge';
import EmptyState from '../EmptyState';
import Icon from '../Icon';
import {
  PLAN_KEYS, PLAN_FEATURES, PLAN_LIMITS, priceFor, formatMoney, formatDate, formatNumber, renewalInfo,
} from '../../../services/billingService';

/**
 * Detailed plan management - a plan picker (Solo / Pro), a billing-cycle switch,
 * and cancel / resume. The Cancel action stays clearly visible at the bottom.
 */
export default function SubscriptionSection({
  billing,
  onSetBilling,
  onSetPlan,
  onCancel,
  onResume,
  onChoosePlan,
}) {
  const { identity, subscription: sub, trial, status, offers } = billing || {};
  const isFounding = identity?.isFounding;
  const canceled = sub?.canceled;
  // Next tier up, if any - drives the quick "Upgrade Plan" action.
  const planIdx = sub ? PLAN_KEYS.indexOf(sub.plan) : -1;
  const upgradeTo = planIdx >= 0 && planIdx < PLAN_KEYS.length - 1 ? PLAN_KEYS[planIdx + 1] : null;

  // Computed from the real prices rather than asserted. The two hardcoded claims that used
  // to sit in this file ("roughly two months free" and "saves 17%") were both wrong for the
  // public plans, which are ~31% off, and there was nothing tying either number to Stripe.
  const annualNote = (() => {
    const m = offers?.[`${String(sub?.plan || 'solo').toLowerCase()}_monthly`];
    const y = offers?.[`${String(sub?.plan || 'solo').toLowerCase()}_yearly`];
    if (!m?.amountCents || !y?.amountCents) return 'Annual billing costs less than paying monthly.';
    const monthsFree = Math.floor(12 - y.amountCents / m.amountCents);
    const pct = Math.round((1 - y.amountCents / (m.amountCents * 12)) * 100);
    return monthsFree >= 1
      ? `Annual billing gives you ${monthsFree} month${monthsFree === 1 ? '' : 's'} free versus paying monthly (save ${pct}%).`
      : `Annual billing saves ${pct}% versus paying monthly.`;
  })();

  if (!sub) {
    return (
      <>
        <SectionHead title="Subscription" subtitle="Choose a plan to start your free trial." />
        <AccountCard title="Plan">
          <EmptyState
            icon="📦"
            title="No active subscription"
            description="You haven't selected a plan yet. Start your 7-day free trial - no credit card required."
            action={<button className={styles.btnPrimary} onClick={onChoosePlan}>Choose a plan</button>}
          />
        </AccountCard>
      </>
    );
  }

  return (
    <>
      <SectionHead
        title="Subscription"
        subtitle="Change your plan or billing cycle. Cancel any time - no strings attached."
      />

      {/* ── Current plan summary ── */}
      <AccountCard
        title="Current plan"
        description="Your plan, price and renewal at a glance."
        actions={<StatusBadge status={status} />}
      >
        <div className={styles.infoGrid}>
          <InfoRow label="Plan" icon={<Icon name="package" size={13} />} strong accent>{isFounding ? `Founding ${sub.plan}` : sub.plan}</InfoRow>
          <InfoRow label="Price" icon={<Icon name="creditCard" size={13} />}>{formatMoney(sub.price, sub.currency)}{sub.unit}</InfoRow>
          <InfoRow label="Billing cycle" icon={<Icon name="calendar" size={13} />}>{sub.billing}</InfoRow>
          <InfoRow label={renewalInfo(sub, trial).label} icon={<Icon name="refresh" size={13} />}>
            {formatDate(renewalInfo(sub, trial).date)}
          </InfoRow>
          {trial?.status === 'active' && (
            <InfoRow label="Trial remaining" icon={<Icon name="zap" size={13} />} accent>{trial.daysLeft} {trial.daysLeft === 1 ? 'day' : 'days'}</InfoRow>
          )}
        </div>
        {isFounding && <p className={styles.note}>🔒 Founding price lock - your rate never rises while you stay subscribed.</p>}
        {upgradeTo && !canceled && (
          <>
            <div className={styles.divider} />
            <div className={styles.cardActions}>
              <button className={styles.btnPrimary} onClick={() => onSetPlan(upgradeTo)}>
                <Icon name="arrowUp" size={14} /> Upgrade to {upgradeTo}
              </button>
              <span className={styles.note} style={{ margin: 0, alignSelf: 'center' }}>
                or change your plan below
              </span>
            </div>
          </>
        )}
      </AccountCard>

      {/* ── What's included ── */}
      <AccountCard title="What's included" description={`Everything in your ${isFounding ? 'Founding ' : ''}${sub.plan} plan.`}>
        <ul className={styles.planCardFeatures}>
          {(PLAN_FEATURES[sub.plan] || []).map((f) => (
            <li key={f} className={styles.planFeature}>
              <span className={styles.planFeatureCheck}><Icon name="check" size={13} /></span>
              {f}
            </li>
          ))}
        </ul>
      </AccountCard>

      {/* ── Usage & limits (future-ready) ── */}
      <AccountCard
        title="Usage & limits"
        description="Your monthly allowances. Live usage tracking is coming soon."
      >
        {(PLAN_LIMITS[sub.plan] || []).map((row) => (
          <div className={styles.usageRow} key={row.key}>
            <div className={styles.usageTop}>
              <span className={styles.usageName}>
                <span className={styles.usageNameIcon}><Icon name={row.icon} size={13} /></span>
                {row.label}
              </span>
              <span className={styles.usageLimit}>
                <b>{formatNumber(row.limit)}</b>{row.unit ? ` ${row.unit}` : ''}
              </span>
            </div>
            {typeof row.limit === 'number' && <div className={styles.usageTrack} />}
          </div>
        ))}
        <div className={styles.trustLine}>
          <span className={styles.trustIcon} aria-hidden="true"><Icon name="clock" size={13} /></span>
          These are your included monthly limits - live usage metering will appear here soon.
        </div>
      </AccountCard>

      {/* ── Billing cycle switch ── */}
      <AccountCard title="Billing cycle" description="Switch between monthly and annual billing.">
        <div className={styles.cardActions}>
          {['Annual', 'Monthly'].map((iv) => (
            <button
              key={iv}
              className={sub.billing === iv ? styles.btnPrimary : styles.btnGhost}
              onClick={() => onSetBilling(iv)}
              disabled={canceled}
            >
              {iv === 'Annual' ? (isFounding ? 'Annual · ≈2 months free' : 'Annual · save 17%') : 'Monthly'}
            </button>
          ))}
        </div>
        <p className={styles.note}>
          {annualNote}{' '}
          Changes take effect from your first billing date.
        </p>
      </AccountCard>

      {/* ── Plan picker ── */}
      <AccountCard title="Change plan" description="Upgrade or downgrade any time. Changes apply from your next billing date.">
        <div className={styles.planGrid}>
          {PLAN_KEYS.map((planKey) => {
            const active = sub.plan === planKey;
            const price = priceFor(offers, planKey, sub.billing);
            return (
              <div key={planKey} className={`${styles.planCard} ${active ? styles.planCardActive : ''}`}>
                <div className={styles.planCardHead}>
                  <span className={styles.planCardName}>{isFounding ? `Founding ${planKey}` : planKey}</span>
                  {active && <StatusBadge status="active" label="Current" />}
                </div>
                <div className={styles.planCardPrice}>
                  <b>{formatMoney(price.amount)}</b> <span>{price.unit}</span>
                </div>
                <ul className={styles.planCardFeatures}>
                  {PLAN_FEATURES[planKey].map((f) => (
                    <li key={f} className={styles.planFeature}>
                      <span className={styles.planFeatureCheck} aria-hidden="true">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                {active ? (
                  <button className={styles.btnGhost} disabled>Current plan</button>
                ) : (
                  <button className={styles.btnPrimary} onClick={() => onSetPlan(planKey)} disabled={canceled}>
                    Switch to {planKey}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </AccountCard>

      {/* ── Cancel / resume - always visible ── */}
      <AccountCard title="Cancel subscription" description="Cancel online in one click - no calls or emails required.">
        {canceled ? (
          <>
            <p className={styles.note} style={{ marginTop: 0 }}>
              Your subscription is set to end on {formatDate(trial?.endsAt)}. You can turn it back on
              any time before then to keep your plan and pricing.
            </p>
            <div className={styles.cardActions} style={{ marginTop: 14 }}>
              <button className={styles.btnPrimary} onClick={onResume}>Resume Subscription</button>
            </div>
          </>
        ) : (
          <>
            <p className={styles.note} style={{ marginTop: 0 }}>
              Cancel whenever you like - it takes one click, and you keep full access until the end of
              your {trial?.status === 'active' ? 'trial' : 'billing period'}. No phone calls, no emails,
              no talking to support.
            </p>
            <div className={styles.cardActions} style={{ marginTop: 14 }}>
              <button className={styles.btnDanger} onClick={onCancel}>Cancel Subscription</button>
            </div>
          </>
        )}
      </AccountCard>
    </>
  );
}
